// The 26-action catalogue, transcribed from the backend `REGISTRY` via the handoff pack
// (`docs/handoffs/platform_actions/` § Actions & payloads). ONE table, four readers: the action
// select filters it by platform, the anchor comes from `level`, the propose form and the wire body
// are both built from `fields`. Adding an action must never mean adding an `if` branch.
//
// `__tests__/catalogueDrift.spec.ts` pins the action NAMES against a fixture of the pack's enum, so
// a backend that adds an action fails here instead of surfacing as a silently missing capability.
//
// Two things the names do not tell you, and that only the registry does:
//   • `keyword.negative_add` / `keyword.negative_remove` are **campaign** level, while every other
//     `keyword.*` is adgroup. Deriving the level from the prefix anchors those two wrongly.
//   • `ad_set.*` and `adgroup.*` are platform FAMILIES, not levels — both sit at `adgroup`.
import type { PlatformActionPlatform } from '../models/platformAction.types'

/** The tier an action targets. `adgroup` covers Google ad groups, TikTok ad groups AND Meta ad sets. */
export type ActionLevel = 'campaign' | 'adgroup' | 'ad'

/**
 * How a payload field is entered and serialised.
 * `nullableNumber` is `spend_cap`, where **null REMOVES the ceiling** — a distinct value, not blank.
 * `list` is comma-entered and always sends an array (an EMPTY one is a meaningful answer).
 * `json` is a blob the write REPLACES wholesale, so it is entered and sent complete.
 */
export type PayloadFieldType =
  'number' | 'nullableNumber' | 'text' | 'nullableText' | 'enum' | 'list' | 'json'

export interface PayloadField {
  /** The wire key — snake_case, sent verbatim. */
  key: string
  type: PayloadFieldType
  /** `true` on every platform, or the platforms that demand it (the registry's `platform_required`). */
  required?: true | readonly PlatformActionPlatform[]
  options?: readonly string[]
}

export interface ActionDescriptor {
  platforms: readonly PlatformActionPlatform[]
  level: ActionLevel
  /** A create produces its target; `target_id` is an OUTPUT and sending one is refused. */
  creates?: true
  fields: readonly PayloadField[]
}

export const ACTION_CATALOGUE: Record<string, ActionDescriptor> = {
  'ad.create': {
    platforms: ['meta'],
    level: 'ad',
    creates: true,
    fields: [
      { key: 'name', type: 'text', required: true },
      { key: 'adset_id', type: 'text', required: true },
      { key: 'creative_id', type: 'text', required: true },
    ],
  },
  'ad.status_update': {
    platforms: ['google', 'meta', 'tiktok'],
    level: 'ad',
    fields: [{ key: 'target_status', type: 'enum', required: true, options: ['active', 'paused'] }],
  },
  'ad_set.bid_strategy': {
    platforms: ['meta'],
    level: 'adgroup',
    fields: [
      {
        key: 'bid_strategy',
        type: 'enum',
        required: true,
        options: [
          'LOWEST_COST_WITHOUT_CAP',
          'LOWEST_COST_WITH_BID_CAP',
          'COST_CAP',
          'LOWEST_COST_WITH_MIN_ROAS',
        ],
      },
      { key: 'bid_amount', type: 'number' },
    ],
  },
  'ad_set.bid_update': {
    platforms: ['meta'],
    level: 'adgroup',
    fields: [{ key: 'bid_amount', type: 'number', required: true }],
  },
  'ad_set.budget_update': {
    platforms: ['meta'],
    level: 'adgroup',
    fields: [{ key: 'daily_budget', type: 'number', required: true }],
  },
  'ad_set.create': {
    platforms: ['meta'],
    level: 'adgroup',
    creates: true,
    fields: [
      { key: 'name', type: 'text', required: true },
      { key: 'targeting', type: 'json', required: true },
      { key: 'billing_event', type: 'text', required: true },
      { key: 'optimization_goal', type: 'text', required: true },
      { key: 'daily_budget', type: 'number' },
      { key: 'lifetime_budget', type: 'number' },
      { key: 'start_time', type: 'text' },
      { key: 'end_time', type: 'text' },
      { key: 'bid_strategy', type: 'text' },
      { key: 'bid_amount', type: 'number' },
      { key: 'promoted_object', type: 'json' },
      { key: 'destination_type', type: 'text' },
    ],
  },
  'ad_set.schedule_update': {
    platforms: ['meta'],
    level: 'adgroup',
    fields: [
      { key: 'start_time', type: 'text' },
      { key: 'end_time', type: 'nullableText' },
    ],
  },
  'ad_set.spend_cap': {
    platforms: ['meta'],
    level: 'adgroup',
    fields: [{ key: 'spend_cap', type: 'nullableNumber', required: true }],
  },
  'ad_set.status_update': {
    platforms: ['meta'],
    level: 'adgroup',
    fields: [{ key: 'target_status', type: 'enum', required: true, options: ['active', 'paused'] }],
  },
  'ad_set.targeting_update': {
    platforms: ['meta'],
    level: 'adgroup',
    fields: [{ key: 'targeting', type: 'json', required: true }],
  },
  'adgroup.bid_update': {
    platforms: ['google', 'tiktok'],
    level: 'adgroup',
    fields: [{ key: 'bid_amount', type: 'number', required: true }],
  },
  'adgroup.budget_update': {
    platforms: ['tiktok'],
    level: 'adgroup',
    fields: [{ key: 'daily_budget', type: 'number', required: true }],
  },
  'adgroup.negative_add': {
    platforms: ['google'],
    level: 'adgroup',
    fields: [
      { key: 'keyword_text', type: 'text', required: true },
      { key: 'match_type', type: 'enum', required: true, options: ['EXACT', 'PHRASE', 'BROAD'] },
    ],
  },
  'adgroup.negative_remove': {
    platforms: ['google'],
    level: 'adgroup',
    fields: [
      { key: 'keyword_text', type: 'text', required: true },
      { key: 'match_type', type: 'enum', required: true, options: ['EXACT', 'PHRASE', 'BROAD'] },
    ],
  },
  'adgroup.status_update': {
    platforms: ['google', 'tiktok'],
    level: 'adgroup',
    fields: [{ key: 'target_status', type: 'enum', required: true, options: ['active', 'paused'] }],
  },
  'campaign.bid_strategy': {
    platforms: ['meta'],
    level: 'campaign',
    fields: [
      {
        key: 'bid_strategy',
        type: 'enum',
        required: true,
        options: [
          'LOWEST_COST_WITHOUT_CAP',
          'LOWEST_COST_WITH_BID_CAP',
          'COST_CAP',
          'LOWEST_COST_WITH_MIN_ROAS',
        ],
      },
      { key: 'bid_amount', type: 'number' },
    ],
  },
  'campaign.budget_update': {
    platforms: ['google', 'meta', 'tiktok'],
    level: 'campaign',
    fields: [{ key: 'daily_budget', type: 'number', required: true }],
  },
  'campaign.create': {
    platforms: ['google', 'meta', 'tiktok'],
    level: 'campaign',
    creates: true,
    fields: [
      { key: 'name', type: 'text', required: true },
      { key: 'objective', type: 'text', required: ['meta', 'tiktok'] },
      { key: 'special_ad_categories', type: 'list', required: ['meta'] },
      { key: 'daily_budget', type: 'number', required: ['google'] },
      {
        key: 'bidding_strategy',
        type: 'enum',
        required: ['google'],
        options: [
          'MANUAL_CPC',
          'MAXIMIZE_CONVERSIONS',
          'MAXIMIZE_CONVERSION_VALUE',
          'TARGET_SPEND',
        ],
      },
      {
        key: 'contains_eu_political_advertising',
        type: 'enum',
        required: ['google'],
        options: ['CONTAINS_EU_POLITICAL_ADVERTISING', 'DOES_NOT_CONTAIN_EU_POLITICAL_ADVERTISING'],
      },
      {
        key: 'budget_mode',
        type: 'enum',
        required: ['tiktok'],
        options: ['BUDGET_MODE_DAY', 'BUDGET_MODE_INFINITE'],
      },
      { key: 'lifetime_budget', type: 'number' },
      { key: 'start_time', type: 'text' },
      { key: 'end_time', type: 'text' },
      { key: 'advertising_channel_type', type: 'text' },
    ],
  },
  'campaign.schedule_update': {
    platforms: ['meta'],
    level: 'campaign',
    fields: [
      { key: 'start_time', type: 'text' },
      { key: 'end_time', type: 'nullableText' },
    ],
  },
  'campaign.spend_cap': {
    platforms: ['meta'],
    level: 'campaign',
    fields: [{ key: 'spend_cap', type: 'nullableNumber', required: true }],
  },
  'campaign.status_update': {
    platforms: ['google', 'meta', 'tiktok'],
    level: 'campaign',
    fields: [{ key: 'target_status', type: 'enum', required: true, options: ['active', 'paused'] }],
  },
  'keyword.add': {
    platforms: ['google'],
    level: 'adgroup',
    fields: [
      { key: 'keyword_text', type: 'text', required: true },
      { key: 'match_type', type: 'enum', required: true, options: ['EXACT', 'PHRASE', 'BROAD'] },
    ],
  },
  'keyword.negative_add': {
    platforms: ['google'],
    level: 'campaign',
    fields: [
      { key: 'keyword_text', type: 'text', required: true },
      { key: 'match_type', type: 'enum', required: true, options: ['EXACT', 'PHRASE', 'BROAD'] },
    ],
  },
  'keyword.negative_remove': {
    platforms: ['google'],
    level: 'campaign',
    fields: [
      { key: 'keyword_text', type: 'text', required: true },
      { key: 'match_type', type: 'enum', required: true, options: ['EXACT', 'PHRASE', 'BROAD'] },
    ],
  },
  'keyword.remove': {
    platforms: ['google'],
    level: 'adgroup',
    fields: [
      { key: 'keyword_text', type: 'text', required: true },
      { key: 'match_type', type: 'enum', required: true, options: ['EXACT', 'PHRASE', 'BROAD'] },
    ],
  },
  'keyword.status_update': {
    platforms: ['google'],
    level: 'adgroup',
    fields: [
      { key: 'keyword_text', type: 'text', required: true },
      { key: 'match_type', type: 'enum', required: true, options: ['EXACT', 'PHRASE', 'BROAD'] },
      { key: 'target_status', type: 'enum', required: true, options: ['active', 'paused'] },
    ],
  },
}
